import type { DetectedCategory, ProductPresentation } from '@pages/Menu/Products/MenuUploaderV2/types/menuUpload.types';

/**
 * Draft del trabajo de revisión (selecciones, ediciones, decisiones de
 * duplicados) para sobrevivir un reload de página durante la fase review.
 *
 * Complementa a `pendingJob.ts`: aquel permite re-obtener el RESULTADO del
 * análisis por jobId; este conserva la CURADURÍA que el usuario hizo encima.
 * Se guarda keyed por jobId — los ids sintéticos del análisis son estables
 * entre resumes del mismo job (vienen del resultado persistido en backend),
 * así que selecciones y patches re-aplican 1:1.
 *
 * Mismo patrón defensivo que pendingJob: nunca lanzar, validar shape, TTL.
 */

export interface ReviewDraft {
  jobId: string;
  savedAt: number;
  selectedCategoryIds: string[];
  selectedProductIds: string[];
  selectedExtraIds: string[];
  selectedSectionIds: string[];
  editedProductPatches: Record<string, { nombre?: string; precio?: number; opciones?: ProductPresentation[] }>;
  editedCategoryNames: Record<string, string>;
  editedExtraPatches: Record<string, { nombre?: string; precio?: number }>;
  editedSectionNames: Record<string, string>;
  duplicateDecisions: Record<number, 'keep_representative' | 'keep_all'>;
  /** Reasignaciones de categoría por producto (productId → categoryId). */
  categoryAssignments?: Record<string, string>;
  /** Categorías creadas client-side desde el EditSheet (id `synthetic_<slug>`). */
  syntheticCategories?: DetectedCategory[];
  /**
   * WS-B: overrides del usuario al matching de categorías existentes
   * (detectedCategoryId → existingCategoryId, o `null` = desvinculada). Solo las
   * DECISIONES manuales; las asociaciones automáticas se recomputan del catálogo
   * al rehidratar. Los targets stale se descartan en uso (guard del publish).
   */
  existingCategoryLinkOverrides?: Record<string, string | null>;
}

export const REVIEW_DRAFT_KEY = 'plick_smart_menu_review_draft';

/** Alineado al TTL del pending job: pasado un día, se empieza de cero. */
export const REVIEW_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const safeStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const isReviewDraft = (val: unknown): val is ReviewDraft => {
  if (typeof val !== 'object' || val === null) return false;
  const draft = val as Partial<ReviewDraft>;
  return (
    typeof draft.jobId === 'string' &&
    draft.jobId.length > 0 &&
    typeof draft.savedAt === 'number' &&
    Array.isArray(draft.selectedProductIds) &&
    Array.isArray(draft.selectedCategoryIds) &&
    Array.isArray(draft.selectedExtraIds) &&
    Array.isArray(draft.selectedSectionIds)
  );
};

/**
 * Guarda el draft (sobrescribe el anterior — solo hay un review activo a la vez).
 *
 * @param {ReviewDraft} draft Estado curado del review keyed por jobId.
 */
export const saveReviewDraft = (draft: ReviewDraft): void => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.setItem(REVIEW_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* cuota llena o storage bloqueado: el flujo sigue sin draft */
  }
};

/**
 * Carga el draft si existe, es válido, no excede TTL y corresponde al jobId dado.
 *
 * @param {string} jobId Job activo — drafts de otros jobs se ignoran (y limpian).
 * @returns {ReviewDraft | null} Draft re-aplicable, o null.
 */
export const loadReviewDraft = (jobId: string): ReviewDraft | null => {
  const storage = safeStorage();
  if (!storage) return null;
  const raw = storage.getItem(REVIEW_DRAFT_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isReviewDraft(parsed) || Date.now() - parsed.savedAt > REVIEW_DRAFT_MAX_AGE_MS) {
      storage.removeItem(REVIEW_DRAFT_KEY);
      return null;
    }
    if (parsed.jobId !== jobId) return null;
    return parsed;
  } catch {
    storage.removeItem(REVIEW_DRAFT_KEY);
    return null;
  }
};

/** Elimina el draft (al publicar con éxito o reiniciar desde cero). */
export const clearReviewDraft = (): void => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(REVIEW_DRAFT_KEY);
  } catch {
    /* no-op */
  }
};
